/**
 * Positive keyboard-mash evidence (D31): the gibberish register fires only
 * when a token AFFIRMATIVELY looks like mash — never on mere absence of
 * recognition.
 *
 * @remarks
 * D28's original rule inverted the burden of proof: any input whose tokens
 * missed a ~600-word allowlist was declared "not language", so `remove chain`
 * earned the mash egg while `Remove the chain` — same words plus an article —
 * did not (Calder's D13-amendment brief, the sharpest repro). An allowlist
 * cannot decide whether English is English. This module supplies the other
 * direction: deterministic, dictionary-free shape evidence that a token was
 * TYPED rather than meant —
 *
 *   - no vowels at length ≥ 3 (`zxcv`, `jkl`, `xkcdq`),
 *   - a same-character run of ≥ 3 (`zzzzz`),
 *   - a directed keyboard-row run of ≥ 3 (`asdf`, `sdasd` contains `asd`,
 *     `qwerty`, `wasd` contains `asd`).
 *
 * Unknown-but-word-shaped tokens (`flumph`, `search`, `chain`) show none of
 * these and get the benefit of the doubt: absence of proof is not proof of
 * absence, and the failure mode was insulting a paying visitor.
 *
 * @packageDocumentation
 */

// cspell:ignore zxcv xkcdq sdasd flumph wasd qwertyuiop asdfghjkl zxcvbnm -- deliberate mash, keyboard rows, and non-words the module is about

/** The QWERTY rows, left to right — directed runs inside them read as finger-drag. */
const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'] as const;

/** Vowels for the shape test (`y` counts — `why`, `gym`, `tryst` are words). */
const VOWEL_PATTERN = /[aeiouy]/u;

/** A same-character run of three or more (`zzz`, `aaaa`). */
const REPEAT_RUN_PATTERN = /(.)\1{2,}/u;

/** Letters only — tokens with digits/punctuation are judged by their letters. */
const NON_LETTER_PATTERN = /[^a-z]/gu;

/**
 * Does one folded token affirmatively look like keyboard mash?
 *
 * @param word - The token's surface text (folded/lowercase)
 * @returns True only on positive shape evidence — never on unfamiliarity
 */
export const looksLikeKeyboardMash = (word: string): boolean => {
	const letters = word.replace(NON_LETTER_PATTERN, '');

	if (letters.length < 3) {
		return false;
	}

	if (!VOWEL_PATTERN.test(letters)) {
		return true;
	}

	if (REPEAT_RUN_PATTERN.test(letters)) {
		return true;
	}

	return KEYBOARD_ROWS.some((keyboardRow) => {
		for (let startIndex = 0; startIndex + 3 <= letters.length; startIndex += 1) {
			if (keyboardRow.includes(letters.slice(startIndex, startIndex + 3))) {
				return true;
			}
		}

		return false;
	});
};
